import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Edit, Trash2, X, Building2, Loader2, AlertCircle } from 'lucide-react';
import { fetchCompanies, addCompany, updateCompany, deleteCompany } from '../actions/companyActions';
import { getGames } from '../actions/gameActions';
import GameSelection from './GameSelection';
import styles from '../styles/ManageCompany.module.css';
import { useAppToast } from '../hooks/useAppToast';
import { useFieldPrivilege } from '../hooks/useFieldPrivilege';

const ManageCompany = () => {
  const dispatch = useDispatch();
  const { showSuccess, showApiError } = useAppToast();
  const { canShowField } = useFieldPrivilege();
  const { companies, loading: companiesLoading, error: companiesError } = useSelector(state => state.company);
  const { games, loading: gamesLoading, error: gamesError } = useSelector(state => state.game);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const [selectedCompany, setSelectedCompany] = useState(null);
  const initialCompanyState = { 
    name: '', 
    slug: '', 
    gameIds: [], 
    is_active: true,
    isseparateresultbygame: false,
    separateResultGameIds: []
  };
  const [companyData, setCompanyData] = useState(initialCompanyState);

  useEffect(() => {
    dispatch(fetchCompanies());
    dispatch(getGames());
  }, [dispatch]);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setCompanyData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const handleGameSelection = useCallback((gameId) => {
    setCompanyData(prevData => ({
      ...prevData,
      gameIds: prevData.gameIds.includes(gameId)
        ? prevData.gameIds.filter(id => id !== gameId)
        : [...prevData.gameIds, gameId]
    }));
  }, []);

  const handleSeparateResultGameSelection = useCallback((gameId) => {
    setCompanyData(prevData => ({
      ...prevData,
      separateResultGameIds: prevData.separateResultGameIds.includes(gameId)
        ? prevData.separateResultGameIds.filter(id => id !== gameId)
        : [...prevData.separateResultGameIds, gameId]
    }));
  }, []);

  const handleAddCompany = useCallback(() => {
    setSelectedCompany(null);
    setCompanyData(initialCompanyState);
    setIsModalOpen(true);
  }, []);

  const handleEditCompany = useCallback((company) => {
    setSelectedCompany(company);
    setCompanyData({
      ...company,
      gameIds: company.gameIds || [],
      isseparateresultbygame: company.isseparateresultbygame ?? false,
      separateResultGameIds: company.separateResultGameIds || []
    });
    setIsModalOpen(true);
  }, []);

  const handleDeleteCompany = useCallback((companyId) => {
    if (window.confirm('Are you sure you want to delete this company?')) {
      dispatch(deleteCompany(companyId))
        .then(() => {
          showSuccess('Company deleted successfully.');
        })
        .catch((error) => {
          showApiError(error, 'Error deleting company');
        });
    }
  }, [dispatch, showSuccess, showApiError]);

  const handleSubmit = useCallback(() => {
    const action = selectedCompany ? updateCompany(selectedCompany._id, companyData) : addCompany(companyData);
    dispatch(action)
      .then(() => {
        showSuccess(`Company ${selectedCompany ? 'updated' : 'added'} successfully.`);
        setIsModalOpen(false);
        setCompanyData(initialCompanyState);
      })
      .catch((error) => {
        showApiError(error, `Error ${selectedCompany ? 'updating' : 'adding'} company`);
      });
  }, [dispatch, selectedCompany, companyData, showSuccess, showApiError]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCompanyData(initialCompanyState);
    setSelectedCompany(null);
  };

  if (companiesLoading || gamesLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader2 className={styles.spinner} size={48} />
      </div>
    );
  }

  if (companiesError || gamesError) {
    return (
      <div className={styles.errorAlert}>
        <AlertCircle size={20} />
        <span>{companiesError || gamesError}</span>
      </div>
    );
  }

  return (
    <div className={styles.manageCompany}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Building2 size={24} className={styles.headerIcon} />
          <h1 className={styles.title}>Manage Companies</h1>
        </div>
        <button className={styles.addButton} onClick={handleAddCompany}>
          <Plus size={20} />
          <span>Add Company</span>
        </button>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              {canShowField('company', 'name', 'list') && <th>Name</th>}
              {canShowField('company', 'slug', 'list') && <th>Slug</th>}
              {canShowField('company', 'is_active', 'list') && <th>Status</th>}
              {canShowField('company', 'gameIds', 'list') && <th>Games</th>}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {companies.length === 0 ? (
              <tr>
                <td
                  colSpan={
                    1 +
                    ['name', 'slug', 'is_active', 'gameIds'].filter((f) =>
                      canShowField('company', f, 'list')
                    ).length
                  }
                  className={styles.emptyState}
                >
                  No companies found. Click "Add Company" to create one.
                </td>
              </tr>
            ) : (
              companies.map((company) => (
                <tr key={company._id}>
                  {canShowField('company', 'name', 'list') && <td>{company.name}</td>}
                  {canShowField('company', 'slug', 'list') && <td>{company.slug}</td>}
                  {canShowField('company', 'is_active', 'list') && (
                    <td>
                      <span className={`${styles.statusBadge} ${company.is_active ? styles.statusActive : styles.statusInactive}`}>
                        {company.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  )}
                  {canShowField('company', 'gameIds', 'list') && (
                    <td>{company.gameIds ? company.gameIds.length : 0} games</td>
                  )}
                  <td>
                    <div className={styles.actionButtons}>
                      <button
                        className={styles.iconButton}
                        onClick={() => handleEditCompany(company)}
                        title="Edit"
                        aria-label="Edit"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        className={`${styles.iconButton} ${styles.deleteButton}`}
                        onClick={() => handleDeleteCompany(company._id)}
                        title="Delete"
                        aria-label="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={handleCloseModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                {selectedCompany ? 'Edit Company' : 'Add Company'}
              </h2>
              <button className={styles.closeButton} onClick={handleCloseModal} aria-label="Close">
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              {canShowField('company', 'name', 'form') && (
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Company Name <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={companyData.name}
                  onChange={handleInputChange}
                  placeholder="Enter company name"
                  className={styles.input}
                  required
                />
              </div>
              )}
              {canShowField('company', 'slug', 'form') && (
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Slug <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="slug"
                  value={companyData.slug}
                  onChange={handleInputChange}
                  placeholder="Enter slug"
                  className={styles.input}
                  required
                />
              </div>
              )}
              {canShowField('company', 'is_active', 'form') && (
              <div className={styles.formGroup}>
                <label className={styles.switchLabel}>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={companyData.is_active}
                    onChange={handleInputChange}
                    className={styles.switch}
                  />
                  <span className={styles.switchText}>Active</span>
                </label>
              </div>
              )}
              {canShowField('company', 'gameIds', 'form') && (
              <div className={styles.formGroup}>
                <label className={styles.label}>Games</label>
                <p className={styles.helpText}>
                  Select the games available for this company:
                </p>
                <GameSelection
                  games={games}
                  selectedGames={companyData.gameIds}
                  onGameSelection={handleGameSelection}
                />
              </div>
              )}
              {canShowField('company', 'isseparateresultbygame', 'form') && (
              <div className={styles.formGroup}>
                <label className={styles.switchLabel}>
                  <input
                    type="checkbox"
                    name="isseparateresultbygame"
                    checked={companyData.isseparateresultbygame}
                    onChange={handleInputChange}
                    className={styles.switch}
                  />
                  <span className={styles.switchText}>Separate Result By Game</span>
                </label>
              </div>
              )}
              {companyData.isseparateresultbygame &&
                canShowField('company', 'separateResultGameIds', 'form') && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>Games for Separate Results</label>
                  <p className={styles.helpText}>
                    Select games for which results should be separated:
                  </p>
                  <GameSelection
                    games={games.filter(g => companyData.gameIds.includes(g._id))}
                    selectedGames={companyData.separateResultGameIds}
                    onGameSelection={handleSeparateResultGameSelection}
                  />
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.submitButton} onClick={handleSubmit}>
                {selectedCompany ? 'Update' : 'Add'}
              </button>
              <button className={styles.cancelButton} onClick={handleCloseModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCompany;
